import Role from '@/enums/role';
import Vue from 'vue';
import toast from '@/services/toasts/index.js';
import { VuexBuilder } from '@helpers/vuexBuilder';

const state = {
  availableFeatures: [],
  availableFeaturesLoaded: false,
  securityGroups: [],
  securityGroupsLoaded: false,
  operations: {
    getAvailableFeatures: false,
    getSecurityGroups: false,
    creatingSecurityGroup: false,
    updatingSecurityGroup: false,
    deletingSecurityGroup: false
  },
};

const getters = {
  hasLoadedAvailableFeatures: (state) => state.availableFeaturesLoaded,
  availableFeatures: (state) => state.availableFeatures,
  isLoadingAvailableFeatures: (state) => state.operations.getAvailableFeatures,
  hasLoadedSecurityGroups: (state) => state.securityGroupsLoaded,
  securityGroups: (state) => state.securityGroups,
  isLoadingSecurityGroups: (state) => state.operations.getSecurityGroups,
  isSavingSecurityGroup: (state) => state.operations.creatingSecurityGroup || state.operations.updatingSecurityGroup,
  isDeletingSecurityGroup: (state) => state.operations.deletingSecurityGroup || state.operations.deletingSecurityGroup,

  /**
   * Retrieves privileges from the loggedInEmployee in the root store.
   */
  privileges: (state, getters, rootState, rootGetters) => {
    const employee = rootGetters.loggedInEmployee;
    if (!employee) return { features: [], roles: [] };

    return employee.privileges || { features: [], roles: [] };
  },

  /**
   * Checks if the logged-in employee has a specific role.
   * @param {string} role - The role to check for.
   * @returns {boolean} Whether the employee has the role.
   */
  hasRole: (state, getters) => (role) => {
    return getters.privileges.roles && getters.privileges.roles.includes(role);
  },

  /**
   * Checks if the logged-in employee is an admin.
   * @returns {boolean} Whether the employee is an admin.
   */
  isAdmin: (state, getters) => {
    return getters.hasRole(Role.ADMIN);
  },

  /**
   * Checks if the logged-in employee has access to a specific feature and action.
   * @param {string} featureId - The feature ID to check.
   * @param {string} action - The action to check within the feature.
   * @returns {boolean} Whether the employee has access to the feature and action.
   */
  hasFeatureAccess: (state, getters) => (featureId, action) => {

    if (getters.isAdmin) return true;

    const feature = getters.privileges.features.find((f) => f.featureId === featureId);

    if (!feature) return false;

    return feature.actions.includes(action);
  },
};

const actions = {
  /**
   * Fetches the list of available features from the API and commits it to the store.
   * @param {Object} param0 - Vuex context object.
   */
  async getAvailableFeatures({ commit, rootState }) {
    return await new VuexBuilder(commit)
      .operationKey('getAvailableFeatures')
      .request(() => Vue.axios.get(`/api/${rootState.tenantId}/features`))
      .commitData('GET_AVAILABLE_FEATURES_SUCCESS')
      .execute();
  },

  /**
   * Fetches the list of security groups from the API and commits it to the store.
   * @param {Object} param0 - Vuex context object.
   */
  async getSecurityGroups({ commit, rootState }) {
    return await new VuexBuilder(commit)
      .operationKey('getSecurityGroups')
      .request(() => Vue.axios.get(`/api/${rootState.tenantId}/securityGroups`))
      .commitData('GET_SECURITY_GROUPS_SUCCESS')
      .execute();
  },

  async createSecurityGroup({ commit, rootState }, payload) {
		return await new VuexBuilder(commit)
			.operationKey("creatingSecurityGroup")
			.request(() =>  Vue.axios.post(`/api/${rootState.tenantId}/securityGroups`, payload))
			.commitData('CREATE_SECURITY_GROUP_SUCCESS')
			.successToast('Permission group created successfully.')
			.errorToast((err) => err.response.status === 409
				? toast.error('Failed to create permission group. Name is already in use')
				: toast.error('Failed to create permission group.'))
			.execute()
	},

  async updateSecurityGroup({ commit, rootState }, payload) {
		return await new VuexBuilder(commit)
			.operationKey("updatingSecurityGroup")
			.request(() =>  Vue.axios.put(`/api/${rootState.tenantId}/securityGroups/${payload.id}`, payload))
			.commitData('UPDATE_SECURITY_GROUP_SUCCESS')
			.successToast('Permission group updated successfully.')
			.errorToast((err) => err.response.status === 409
				? toast.error('Failed to update permission group. Name is already in use')
				: toast.error('Failed to update permission group.'))
			.execute()
	},

  async deleteSecurityGroup({ commit, rootState }, id) {
		return await new VuexBuilder(commit)
			.operationKey("deletingSecurityGroup")
			.request(() =>  Vue.axios.delete(`/api/${rootState.tenantId}/securityGroups/${id}`))
			.commitData('DELETE_SECURITY_GROUP_SUCCESS', id)
			.successToast('Permission group deleted successfully.')
			.errorToast('Failed to delete permission group.')
			.execute()
	},
};

const mutations = {
  /**
   * Marks an operation as started.
   * @param {Object} state - The Vuex state object.
   * @param {string} operationKey - The operation key to mark as started.
   */
  START_OPERATION(state, operationKey) {
    state.operations[operationKey] = true;
  },

  /**
   * Marks an operation as finished.
   * @param {Object} state - The Vuex state object.
   * @param {string} operationKey - The operation key to mark as finished.
   */
  FINISH_OPERATION(state, operationKey) {
    state.operations[operationKey] = false;
  },

  /**
   * Commits the fetched list of available features to the state.
   * @param {Object} state - The Vuex state object.
   * @param {Array} data - The list of available features.
   */
  GET_AVAILABLE_FEATURES_SUCCESS(state, data) {
    state.availableFeatures = data;
    state.availableFeaturesLoaded = true;
  },

  /**
   * Commits the fetched list of security groups to the state.
   * @param {Object} state - The Vuex state object.
   * @param {Array} data - The list of security groups.
   */
  GET_SECURITY_GROUPS_SUCCESS(state, data) {
    state.securityGroups = data;
    state.securityGroupsLoaded = true;
  },

  CREATE_SECURITY_GROUP_SUCCESS(state, data) {
    state.securityGroups.push(data);
  },

  UPDATE_SECURITY_GROUP_SUCCESS(state, data) {
    let foundIndex = state.securityGroups.findIndex(x => x.id === data.id);
		if (foundIndex > -1) {
			state.securityGroups.splice(foundIndex, 1, data);
		}
  },

  DELETE_SECURITY_GROUP_SUCCESS(state, id) {
    const index = state.securityGroups.findIndex(a => a.id === id);
		if (index > -1) {
			state.securityGroups.splice(index, 1);
		}
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
